





























import Vue from 'vue';
import {Component, Emit, Prop, Watch} from "vue-property-decorator";
import AsiCard from "@/components/common/AsiCard.vue";
import {Icon} from "@/helpers/icons";
import User from "@/models/user/User";
import AsiContentContainer from "@/components/common/AsiContentContainer.vue";
import {CompanyType, Permissions, Roles} from "@/helpers/constants";
import AsiBtn from "@/components/common/AsiBtn.vue";
import AsiCheckbox from "@/components/common/AsiCheckbox";
import AsiSwitch from "@/components/common/AsiSwitch";
import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
import {SnackbarMessage} from "@/models/SnackbarMessage";

@Component({
	components: {AsiConfirmDialog, AsiSwitch, AsiCheckbox, AsiBtn, AsiContentContainer, AsiCard}
})
export default class UserCardRightsOffice extends Vue {

	@Prop({type: Object, default: null})
	public user!: User | null;

	@Prop({type: Array, required: true})
	public roles!: Roles[];

	@Prop({type: Array, required: true})
	public permissions!: Permissions[];

	private loadingRole: boolean = false;
	private loadingPermissionMasterData: boolean = false;
	private loadingPermissionCheckReports: boolean = false;
	private loadingPermissionPlanning: boolean = false;
	private loadingPermissionBilling: boolean = false;
	private hasPermissionMasterDataInternal: boolean = false;
	private hasPermissionCheckReportsInternal: boolean = false;
	private hasPermissionPlanningInternal: boolean = false;
	private hasPermissionBillingInternal: boolean = false;

	private get icon(): string {
		return this.hasRole ? Icon.yes : Icon.no;
	}

	private get iconBgColor(): string {
		return this.hasRole ? 'success' : 'error';
	}

	private get buttonColor(): string {
		return this.hasRole ? 'error' : 'success';
	}

	private get isAdmin(): boolean {
		return this.roles.includes(Roles.roleAdmin);
	}

	private get hasRole(): boolean {
		return this.isAdmin || this.roles.includes(Roles.roleOffice);
	}

	private get hasPermissionMasterData(): boolean {
		return this.isAdmin || this.permissions.includes(Permissions.rightMasterData);
	}

	private set hasPermissionMasterData(value: boolean) {
		if (this.user === null) return;

		this.loadingPermissionMasterData = true;
		if (value) {
			this.$userService.assignPermission(this.user.id, Permissions.rightMasterData)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionMasterData = false;
					this.rightsChanged();
				});
		} else {
			this.$userService.revokePermission(this.user.id, Permissions.rightMasterData)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionMasterData = false;
					this.rightsChanged();
				});
		}
	}

	private get hasPermissionCheckReports(): boolean {
		return this.isAdmin || this.permissions.includes(Permissions.rightCheckReports);
	}

	private set hasPermissionCheckReports(value: boolean) {
		if (this.user === null) return;

		this.loadingPermissionCheckReports = true;
		if (value) {
			this.$userService.assignPermission(this.user.id, Permissions.rightCheckReports)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionCheckReports = false;
					this.rightsChanged();
				});
		} else {
			this.$userService.revokePermission(this.user.id, Permissions.rightCheckReports)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionCheckReports = false;
					this.rightsChanged();
				});
		}
	}

	private get hasPermissionPlanning(): boolean {
		return this.isAdmin || this.permissions.includes(Permissions.rightPlanning);
	}

	private set hasPermissionPlanning(value: boolean) {
		if (this.user === null) return;

		this.loadingPermissionPlanning = true;
		if (value) {
			this.$userService.assignPermission(this.user.id, Permissions.rightPlanning)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionPlanning = false;
					this.rightsChanged();
				});
		} else {
			this.$userService.revokePermission(this.user.id, Permissions.rightPlanning)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionPlanning = false;
					this.rightsChanged();
				});
		}
	}

	private get hasPermissionBilling(): boolean {
		return this.isAdmin || this.permissions.includes(Permissions.rightBilling);
	}

	private set hasPermissionBilling(value: boolean) {
		if (this.user === null) return;

		this.loadingPermissionBilling = true;
		if (value) {
			this.$userService.assignPermission(this.user.id, Permissions.rightBilling)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionBilling = false;
					this.rightsChanged();
				});
		} else {
			this.$userService.revokePermission(this.user.id, Permissions.rightBilling)
				.catch(this.updateError)
				.finally(() => {
					this.loadingPermissionBilling = false;
					this.rightsChanged();
				});
		}
	}

	private get isInternalCompany(): boolean {
		return this.user?.company.type === CompanyType.internal;
	}

	public created(): void {
		this.setInternalPermissions();
	}

	@Emit('rightsChanged')
	public rightsChanged(): void {
		return;
	}

	@Watch('permissions')
	private onPermissionsChanged(): void {
		this.setInternalPermissions();
	}

	private setInternalPermissions(): void {
		this.hasPermissionMasterDataInternal = this.permissions.includes(Permissions.rightMasterData);
		this.hasPermissionCheckReportsInternal = this.permissions.includes(Permissions.rightCheckReports);
		this.hasPermissionPlanningInternal = this.permissions.includes(Permissions.rightPlanning);
		this.hasPermissionBillingInternal = this.permissions.includes(Permissions.rightBilling);
	}

	private toggleRole(): void {
		if (this.user === null) return;

		this.loadingRole = true;
		if (!this.hasRole) {
			this.$userService.assignRole(this.user.id, Roles.roleOffice)
				.then(this.rightsChanged)
				.catch(this.updateError)
				.finally(() => this.loadingRole = false);
		} else {
			this.$userService.revokeRole(this.user.id, Roles.roleOffice)
				.then(this.rightsChanged)
				.catch(this.updateError)
				.finally(() => this.loadingRole = false);
		}
	}

	private updateError(): void {
		this.$store.commit('ui/showSnackbar', {
			message: this.$t('snackbar.updateError'),
			color: 'error',
		} as SnackbarMessage);
	}

}
